import Location from "./collisionWarning/Location";

export const ITERATION_MAXIMUM = 1000;

// 回転楕円体長半径 a
export const SEMI_MAJOR_AXIS = 6378137.0;

// 回転楕円体短半径 b
export const SEMI_MINOR_AXIS = 6356752.314245;

// 回転楕円体離心率 e^2
export const SQUARED_ECCENTRICITY = 0.00669437999019758;

// 扁平率f
export const FLATTENING = 1.0 - SEMI_MINOR_AXIS / SEMI_MAJOR_AXIS;

/**
 * 始点と終点の緯度経度から、始点から出発する方向角と距離を求める
 * Vincentyの逆解法による
 * @param fromLocation 始点の緯度経度
 * @param toLocation 終点の緯度経度
 * @return {Object} 方位角
 * @return null 2点間の緯度経度が同じ場合
 */
export const getMovement = (fromLocation, toLocation) => {
  // 2点間の緯度経度が同じ場合、計算結果がNaNなるのでnullを返却
  if (
    fromLocation.longitude === toLocation.longitude &&
    fromLocation.latitude === toLocation.latitude
  ) {
    return null
  }

  const u1 = Math.atan(
    (1.0 - FLATTENING) * Math.tan((Math.PI * fromLocation.latitude) / 180.0)
  );
  const u2 = Math.atan(
    (1.0 - FLATTENING) * Math.tan((Math.PI * toLocation.latitude) / 180.0)
  );
  const sinU1 = Math.sin(u1);
  const cosU1 = Math.cos(u1);
  const sinU2 = Math.sin(u2);
  const cosU2 = Math.cos(u2);
  const diffLon =
    (Math.PI * (fromLocation.longitude - toLocation.longitude)) / 180.0;
  let beforeLambda = Infinity;
  let lambda = diffLon;
  let counter = 0;
  for (;;) {
    counter += 1;
    const sinLambda = Math.sin(lambda);
    const cosLambda = Math.cos(lambda);
    const sinSigma =
      Math.sqrt(Math.pow(cosU2 * sinLambda, 2.0)) +
      Math.pow(cosU1 * sinU2 - sinU1 * cosU2 * cosLambda, 2.0);
    const cosSigma = sinU1 * sinU2 + cosU1 * cosU2 * cosLambda;
    const sigma = Math.atan2(sinSigma, cosSigma);
    const sinAlpha = (cosU1 * cosU2 * sinLambda) / sinSigma;
    const cos2Alpha = 1.0 - Math.pow(sinAlpha, 2.0);
    const cos2sm = cosSigma - (2.0 * sinU1 * sinU2) / cos2Alpha;
    const capC =
      (FLATTENING / 16.0) *
      cos2Alpha *
      (4.0 + FLATTENING * (4.0 - 3.0 * cos2Alpha));
    beforeLambda = lambda;
    lambda =
      diffLon +
      (1.0 - capC) *
        FLATTENING *
        sinAlpha *
        (sigma +
          capC *
            sinSigma *
            (cos2sm + capC * cosSigma * (-1.0 + 2.0 * Math.pow(cos2sm, 2.0))));
    if (
      Math.abs(lambda - beforeLambda) <= 1e-12 ||
      counter > ITERATION_MAXIMUM
    ) {
      const usq = cos2Alpha * SQUARED_ECCENTRICITY;
      const k1 = (Math.sqrt(1.0 + usq) - 1) / (Math.sqrt(1.0 + usq) + 1);
      const capA = (1.0 + Math.pow(k1, 2.0) / 4.0) / (1.0 - k1);
      const capB = k1 * (1.0 - (3.0 * Math.pow(k1, 2.0)) / 8.0);
      const deltaSigma =
        capB *
        sinSigma *
        (cos2sm +
          (capB / 4.0) *
            (cosSigma * (-1.0 + 2.0 * Math.pow(cos2sm, 2.0)) -
              (capB / 6.0) *
                cos2sm *
                (-3.0 * 4.0 * Math.pow(sinSigma, 2.0)) *
                (-3.0 * 4.0 * Math.pow(cos2sm, 2.0))));
      const dist = SEMI_MINOR_AXIS * capA * (sigma - deltaSigma);
      let direction = -Math.atan2(
        cosU1 * sinLambda,
        cosU1 * sinU2 - sinU1 * cosU2 * cosLambda
      );
      if (direction < 0) {
        direction += 2.0 * Math.PI;
      }
      let endDirection = -Math.atan2(
        cosU1 * sinLambda,
        -sinU1 * cosU2 + cosU1 * sinU2 * cosLambda
      );
      if (endDirection < 0) {
        endDirection += 2.0 * Math.PI;
      }
      return {
        distance: dist,
        direction: (direction * 180.0) / Math.PI,
        endDirection: (endDirection * 180.0) / Math.PI
      };
    }
  }
};

/**
 * 始点からある方向にある距離進んだときの位置の緯度経度を求める
 * Vincentyの順解法による
 * @param location 始点の緯度経度
 * @param direction 方向角(北を0度として時計回りに360度まで)
 * @param distanceInMeter 距離(単位はメートル)
 */
export const getArrivalPoint = (location, direction, distanceInMeter) => {
  const a1 = (direction * Math.PI) / 180.0;
  const cosA1 = Math.cos(a1);
  const sinA1 = Math.sin(a1);
  const u1 = Math.atan(
    (1.0 - FLATTENING) * Math.tan((Math.PI * location.latitude) / 180.0)
  );
  const sinU1 = Math.sin(u1);
  const cosU1 = Math.cos(u1);

  const sigma1 = Math.atan2(Math.tan(u1), cosA1);
  const sinAlpha = cosU1 * sinA1;
  const cos2Alpha = 1.0 - Math.pow(sinAlpha, 2.0);
  const usq = cos2Alpha * SQUARED_ECCENTRICITY;
  const k1 = (Math.sqrt(1.0 + usq) - 1) / (Math.sqrt(1.0 + usq) + 1);
  const capA = (1.0 + Math.pow(k1, 2.0) / 4.0) / (1.0 - k1);
  const capB = k1 * (1.0 - (3.0 * Math.pow(k1, 2.0)) / 8.0);

  let beforeSigma = 0.0;
  let sigma = distanceInMeter / (SEMI_MINOR_AXIS * capA);
  let counter = 0;
  do {
    counter += 1;
    const cos2sm = Math.cos(2.0 * sigma1 + sigma);
    const sinSigma = Math.sin(sigma);
    const cosSigma = Math.cos(sigma);
    const deltaSigma =
      capB *
      sinSigma *
      (cos2sm +
        (capB / 4.0) *
          (cosSigma * (-1.0 + 2.0 * Math.pow(cos2sm, 2.0)) -
            (capB / 6.0) *
              cos2sm *
              (-3.0 + 4.0 * Math.pow(sinSigma, 2.0)) *
              (-3.0 + 4.0 * Math.pow(cos2sm, 2.0))));
    beforeSigma = sigma;
    sigma = distanceInMeter / (SEMI_MINOR_AXIS * capA) + deltaSigma;

    let lat = Math.atan2(
      sinU1 * cosSigma + cosU1 * sinSigma * cosA1,
      (1.0 - FLATTENING) *
        Math.sqrt(
          Math.pow(sinAlpha, 2.0) +
            Math.pow(sinU1 * sinSigma - cosU1 * cosSigma * cosA1, 2.0)
        )
    );
    if (lat < 0) {
      lat += 2.0 * Math.PI;
    }

    const lambda = Math.atan2(
      sinSigma * sinA1,
      cosU1 * cosSigma - sinU1 * sinSigma * cosA1
    );
    const capC =
      (FLATTENING / 16.0) *
      cos2Alpha *
      (4.0 + FLATTENING * (4.0 - 3.0 * cos2Alpha));
    const diffLon =
      lambda -
      (1.0 - capC) *
        FLATTENING *
        sinAlpha *
        (sigma +
          capC *
            sinSigma *
            (cos2sm + capC * cosSigma * (-1.0 + 2.0 * Math.pow(cos2sm, 2.0))));

    let lon = (location.longitude * Math.PI) / 180.0 + diffLon;
    while (lon < 0) {
      lon += 2.0 * Math.PI;
    }

    return new Location((lat * 180.0) / Math.PI, (lon * 180.0) / Math.PI);
  } while (
    Math.abs(sigma - beforeSigma) > 1e-12 ||
    counter <= ITERATION_MAXIMUM
  );
};
